import { ImgHTMLAttributes } from 'react';

import { getOptimizeSrc, OptimizeSrcProps } from '~utils/browser/getOptimizeSrc';

type ImageProps = OptimizeSrcProps & ImgHTMLAttributes<HTMLImageElement>;

interface PictureProps extends ImageProps {
  mobile?: OptimizeSrcProps;
  resolution2xSrc?: string;
}

/**
 * 1. 이미지 리사이징을 해줍니다.
 * 2. 브라우저가 webp 지원하면 webp포맷으로 내려줍니다.
 * 참고:
 * 보안을 위해 white list로 domain 관리하고 있습니다.
 * white list에 등록되지 않은 domain으로 요청이 들어오면 요청은 응답 거부(400 error) 됩니다.
 * @link https://us-east-1.console.aws.amazon.com/cloudfront/v3/home?region=ap-northeast-2#/functions/image-reize-allow-domain
 * ---------------------------------------------------------------------------------------------------------------------------------
 * @param src 이미지 원본 URL
 * @param alt 이미지 대체 텍스트 값. SEO 에 영향을 주므로 이미지에 맞는 대체텍스트를 넣을 것을 지향합니다.
 * @param width 이미지 가로사이즈이며, 크기에 영향을 줍니다.
 * @param quality 리사이징시 이미지 품질을 나타내는 정수값 입니다. 최상의 품질은 100이며, 기본값은 75 입니다.
 * @see https://wantedlab.atlassian.net/wiki/spaces/FE/pages/2464972903/CloudFront+Lambda+Function+URL+.
 */
const Image = ({ src, alt = '', width, quality = 75, ...other }: ImageProps) => {
  return (
    <img
      src={getOptimizeSrc({
        src,
        width,
        quality,
      })}
      alt={alt}
      {...other}
    />
  );
};

export function Picture({
  mobile,
  resolution2xSrc,
  src,
  alt = '',
  width,
  quality = 75,
  ...props
}: PictureProps) {
  return (
    <picture>
      {mobile && (
        <source
          srcSet={`${getOptimizeSrc({
            src: mobile.src,
            width: mobile.width,
            quality: mobile.quality,
          })} 2x`}
          media='(max-width: 767px)'
        />
      )}
      {resolution2xSrc && (
        <source
          srcSet={`${getOptimizeSrc({
            src: resolution2xSrc,
            width: Number(width) * 2,
            quality,
          })} 2x`}
        />
      )}
      <Image src={src} width={width} quality={quality} alt={alt} {...props} />
    </picture>
  );
}

export default Image;
